import { inject, Injectable } from "@angular/core";
import { FgdsProductType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { catchError, defaultIfEmpty, forkJoin, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SignalElementDataService {
  private productService = inject(ProductService);

  getSignalElementData(signalElements: readonly SignalElementConfiguration[], localeId: string): Observable<ProductData[][]> {
    if (signalElements.length == 0) {
      return of([]);
    }
    return forkJoin(signalElements.map((signalElement) => this.getProducts(signalElement, localeId))).pipe(
      map((products) => products.filter((product): product is ProductData[] => !!product)),
    );
  }

  assignProductToConfiguration(
    configs: SignalElementConfiguration[],
    uniqueConfigs: SignalElementConfiguration[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig) =>
        this.getProducts(uniqueConfig, localeId).pipe(
          map((productData) => {
            const placedProductsCount = configs.filter((config) => config.id === uniqueConfig.id).length;
            return new ConfigProductMap(uniqueConfig, productData ?? [], placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(signalElementConfig: SignalElementConfiguration, localeId: string): Observable<ProductData[] | undefined> {
    return this.productService.getProductById(signalElementConfig.productId).pipe(
      map((product) => {
        if (!product || product.type !== FgdsProductType.SIGNALELEMENT) {
          return undefined;
        }
        const productData = ProductData.create(
          product,
          localeId,
          this.productService.getFilterCountry()!,
          1,
          ProductListDiscountSlot.MAIN,
        );
        productData.positionId = signalElementConfig.positionNumber!;
        return [productData];
      }),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }
}
